import { useCallback, useEffect, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { PlaidService } from '../services/plaid';
import { usePlaidLink as usePlaidLinkSDK } from 'react-plaid-link';
import type { PlaidLinkOnExit, PlaidLinkOnEvent } from 'react-plaid-link';

export const usePlaidLink = () => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const { user } = useUser();
  const navigate = useNavigate();

  const generateToken = useCallback(async () => {
    if (!user?.id || linkToken) {
      return;
    }

    try {
      console.log('Generating link token for user:', user.id);
      const token = await PlaidService.createLinkToken(user.id);
      console.log('Link token generated:', token);
      setLinkToken(token);
    } catch (error) {
      console.error('Error generating link token:', error);
    }
  }, [user?.id, linkToken]);

  useEffect(() => {
    generateToken();
  }, [generateToken]);

  const onSuccess = useCallback(async (publicToken: string) => {
    if (!user?.id) {
      console.error('No user ID available');
      return;
    }

    try {
      console.log('Plaid Link success, exchanging public token');
      // Set data source before starting the process
      sessionStorage.setItem('dataSource', 'plaid');
      
      // Navigate to loading page first
      navigate('/loading');
      
      // Then exchange public token for access token
      await PlaidService.exchangePublicToken(publicToken, user.id);
    } catch (error) {
      console.error('Error in Plaid Link success callback:', error);
      // If there's an error, navigate back to upload page
      navigate('/');
    }
  }, [user?.id, navigate]);

  const onExit: PlaidLinkOnExit = useCallback((error) => {
    console.log('Plaid Link exit:', error || 'No error');
    // If there's an error or user exits, clear the link token
    setLinkToken(null);
  }, []);

  const onEvent: PlaidLinkOnEvent = useCallback((eventName, metadata) => {
    console.log('Plaid Link event:', eventName, metadata);
    // If the user completes the flow, clear the link token
    if (eventName === 'HANDOFF') {
      setLinkToken(null);
    }
  }, []);

  const config = {
    token: linkToken,
    onSuccess,
    onExit,
    onEvent,
  };

  const { open, ready } = usePlaidLinkSDK(config);

  return { open, ready };
};
